import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Check, Plus, X, Pencil, Mail, Bot, Truck } from 'lucide-react';
import { api } from '../lib/api';
import { RagChip, fmtDay, fmtWhen, loadMeta, CONTACT_ROLE_LABEL } from '../lib/shipments';
import { myEmail, amAdmin } from '../lib/taskAccess';
import ShipmentMessages from '../components/ShipmentMessages';
import Modal from '../components/ui/Modal';
import { Field } from './ShipmentsPage';
import { needByLabel } from '../lib/needByLabel.mjs';
import { projectChoices } from '../lib/projectChoices.mjs';

const IDENT_GROUPS = [
  ['Order', [['order_ref', 'Order ref'], ['concria_order_no', 'Concria order #'], ['concria_offer_no', 'Concria offer #'], ['customer_name', 'Customer'], ['project_name', 'Project'], ['customer_po', 'Customer PO'], ['ccs_po', 'CCS PO'], ['need_by_date', 'Need on site by', 'date'], ['need_by_note', 'Need-by note']]],
  ['Freight', [['origin', 'Origin', 'enum:origin'], ['incoterm', 'Incoterm'], ['mode', 'Mode', 'enum:mode'], ['route', 'Route', 'enum:route'], ['forwarder', 'Forwarder'], ['broker', 'Customs broker'], ['carrier', 'Ocean carrier'], ['vessel', 'Vessel'], ['voyage', 'Voyage'], ['cutoff_date', 'Cut-off', 'date'], ['etd', 'ETD', 'date'], ['eta', 'ETA (US port)', 'date'], ['atd', 'Departed (actual)', 'date'], ['ata', 'Arrived (actual)', 'date']]],
  ['Documents & customs', [['container_numbers', 'Containers', 'list'], ['hbl', 'HBL'], ['mbl', 'MBL'], ['isf_number', 'ISF #'], ['entry_number', 'Entry #'], ['last_free_day', 'Last free day', 'date'], ['duty_amount', 'Duty ($)', 'number']]],
  ['Warehouse & delivery', [['pinnacle_job', 'Pinnacle job #'], ['received_count', 'Received count', 'number'], ['outbound_carrier', 'Outbound carrier'], ['outbound_tracking', 'Tracking #'], ['pod_url', 'POD link'], ['freight_cost', 'Freight cost ($)', 'number']]],
];
// Reuse the Identifiers wording for milestone detail fields.
const FIELD_LABEL = Object.fromEntries(IDENT_GROUPS.flatMap(([, f]) => f.map(([k, l]) => [k, l])));

export default function ShipmentDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const [metaError, setMetaError] = useState('');
  const [logOpen, setLogOpenState] = useState(false);
  const [editOpen, setEditOpenState] = useState(false);
  const [ackError, setAckErrorState] = useState('');
  const [timelineError, setTimelineErrorState] = useState('');

  const shownId = useRef(id);
  shownId.current = id;
  // This render's handlers open, close and report on its own shipment only: once the page shows
  // another shipment, a late open, close or error from the earlier one's action is dropped.
  const forThisShipment = (set) => (value) => { if (id === shownId.current) set(value); };
  const setLogOpen = forThisShipment(setLogOpenState);
  const setEditOpen = forThisShipment(setEditOpenState);
  const setAckError = forThisShipment(setAckErrorState);
  const setTimelineError = forThisShipment(setTimelineErrorState);
  const seq = useRef(0); // only the latest load may update the page (an earlier shipment's late answer never lands)
  const load = useCallback(async () => {
    if (id !== shownId.current) return;
    const mine = ++seq.current;
    try { const d = await api.get(`/shipments/${id}`); if (mine === seq.current) { setData(d); setError(''); } } catch (e) { if (mine === seq.current) setError(e.message); }
  }, [id]);
  useEffect(() => {
    // another shipment: show Loading, never the last shipment's details under this address
    setData(null); setError('');
    load();
    loadMeta().then((m) => { setMeta(m); setMetaError(''); }).catch((e) => setMetaError(e.message));
  }, [load]);
  useEffect(() => {
    // another shipment: close the last shipment's Log update and Edit, and clear its action errors
    setLogOpenState(false); setEditOpenState(false); setAckErrorState(''); setTimelineErrorState('');
  }, [id]);

  // Retry missing metadata before opening controls that depend on it.
  async function withMeta(open) {
    if (!meta) {
      try { setMeta(await loadMeta()); setMetaError(''); } catch (e) { setMetaError(e.message); return; }
    }
    open(true);
  }

  // Reload whatever happens so another partner's acknowledgement is reflected.
  async function ackAlert(alertId) {
    setAckError('');
    try { await api.post(`/shipments/alerts/${alertId}/ack`, {}); } catch (e) { setAckError(e.message); } finally { load(); }
  }

  if (error && !data) return <div className="p-4 text-red-600">{error}</div>;
  if (!data) return <div className="p-4 text-[var(--text-secondary)]">Loading…</div>;
  const { shipment: s, events, contacts, alerts, messages } = data;
  const sch = s.schedule || {};
  const labelOf = (k) => meta?.milestones?.find((m) => m.key === k)?.label || k;

  return (
    <div className="mx-auto max-w-5xl pb-36">
      <Link to="/shipments" className="mb-3 inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-primary"><ArrowLeft size={14} /> Shipments</Link>
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {metaError && <p className="mb-3 text-sm text-red-600">Could not load the milestone list: {metaError}</p>}

      {/* Header */}
      <div className="mb-4 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-[var(--text-primary)]">{s.project_name || s.customer_name || s.order_ref}</h1>
            <p className="text-sm text-[var(--text-secondary)]">{s.order_ref}{s.customer_name ? ` · ${s.customer_name}` : ''}{s.origin ? ` · from ${s.origin}` : ''}</p>
          </div>
          <RagChip schedule={sch} big />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <Stat label="Now" value={s.current_milestone_label || 'No update yet'} sub={s.current_milestone_at ? fmtWhen(s.current_milestone_at) : ''} />
          <Stat label="Need on site" value={fmtDay(s.need_by_date)} sub={s.need_by_note || needByLabel(sch.days_to_need_by)} />
          <Stat label="Projected (usual pace)" value={fmtDay(sch.projected_delivery)} sub={sch.slack_days != null ? (sch.slack_days < 0 ? `${-sch.slack_days} d late` : `${sch.slack_days} d slack`) : ''} />
          <Stat label="If a normal delay hits" value={fmtDay(sch.projected_delivery_conservative)} sub={sch.slack_days_conservative != null ? (sch.slack_days_conservative < 0 ? `${-sch.slack_days_conservative} d late` : `${sch.slack_days_conservative} d slack`) : ''} />
        </div>
        {s.data_gaps?.length > 0 && (
          <p className="mt-3 text-xs text-[var(--text-secondary)]">Unknown so far: {s.data_gaps.map((g) => <span key={g} className="mr-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 dark:bg-slate-800">{g}</span>)}</p>
        )}
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Section title={`Alarms (${alerts.length})`}>
          {ackError && <p className="mb-2 text-xs text-red-600">{ackError}</p>}
          <div className="space-y-2">
            {alerts.map((a) => (
              <div key={a.id} className={`flex items-start justify-between gap-2 rounded-lg border p-3 text-sm ${a.severity === 'critical' ? 'border-red-300 bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-200' : 'border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-200'}`}>
                <p className="flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" />{a.message}</p>
                {a.status === 'open' ? (
                  <button onClick={() => ackAlert(a.id)} className="shrink-0 rounded border border-current px-2 py-0.5 text-xs">Got it</button>
                ) : <span className="shrink-0 text-xs opacity-70">acknowledged</span>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Plan */}
      {sch.plan?.length > 0 && (
        <Section title="Remaining steps vs. the need-by date">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-[var(--text-secondary)]">
                <tr><th className="py-1 pr-3">Step</th><th className="py-1 pr-3">Projected</th><th className="py-1 pr-3">Must happen by</th><th className="py-1">Slack</th></tr>
              </thead>
              <tbody>
                {sch.plan.map((p) => (
                  <tr key={p.milestone} className="border-t border-[var(--border)]">
                    <td className="py-1.5 pr-3">{labelOf(p.milestone)}</td>
                    <td className="py-1.5 pr-3">{fmtDay(p.projected)}</td>
                    <td className="py-1.5 pr-3">{fmtDay(p.latest_safe)}</td>
                    <td className={`py-1.5 font-semibold ${p.slack_days == null ? '' : p.slack_days < 0 ? 'text-red-600' : p.slack_days < 7 ? 'text-amber-600' : 'text-green-700'}`}>{p.slack_days == null ? '—' : `${p.slack_days} d`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-[var(--text-secondary)]">Usual pace: production ~1 wk, booking/sailing ~10 d, ocean ~13 d, customs ~3 d, to Pinnacle ~2 d, handling ~1 d, truck ~3 d. A booked ETD/ETA replaces the estimate.</p>
        </Section>
      )}

      {/* Timeline */}
      <Section title="Timeline" action={<button onClick={() => withMeta(setLogOpen)} className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white"><Plus size={12} /> Log update</button>}>
        {timelineError && <p className="mb-2 text-xs text-red-600">{timelineError}</p>}
        {events.length === 0 && <p className="text-sm text-[var(--text-secondary)]">Nothing logged yet.</p>}
        <ol className="space-y-3">
          {events.map((e) => (
            <li key={e.id} className="relative border-l-2 border-[var(--border)] pl-4">
              <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-primary" />
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">{labelOf(e.milestone)}</p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {fmtWhen(e.occurred_at)} · {e.source === 'manual' ? `logged by ${e.created_by || 'someone'}` : <span className="inline-flex items-center gap-1"><Bot size={11} /> from {e.source}</span>}
                  </p>
                </div>
                {(amAdmin() || (e.created_by && e.created_by === myEmail())) && (
                  <button title="Remove this update" onClick={async () => {
                    // eslint-disable-next-line no-alert
                    if (!window.confirm('Remove this update from the timeline?')) return;
                    setTimelineError('');
                    try { await api.delete(`/shipments/${s.id}/events/${e.id}`); } catch (err2) { setTimelineError(err2.message); } finally { load(); }
                  }} className="text-gray-400 hover:text-red-600"><X size={14} /></button>
                )}
              </div>
              {e.note && <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--text-primary)]">{e.note}</p>}
              {e.evidence_text && <blockquote className="mt-1 border-l-2 border-gray-300 pl-2 text-xs italic text-[var(--text-secondary)]">{e.evidence_text}</blockquote>}
              {e.fields && Object.keys(e.fields).length > 0 && (
                <p className="mt-1 text-xs text-[var(--text-secondary)]">{Object.entries(e.fields).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ')}</p>
              )}
            </li>
          ))}
        </ol>
      </Section>

      {/* Identifiers */}
      <Section title="Identifiers" action={<button onClick={() => withMeta(setEditOpen)} className="flex items-center gap-1 rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs font-medium"><Pencil size={12} /> Edit</button>}>
        <div className="grid gap-4 sm:grid-cols-2">
          {IDENT_GROUPS.map(([g, fields]) => (
            <div key={g}>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">{g}</p>
              <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-sm">
                {fields.map(([k, l, t]) => {
                  const v = s[k];
                  const shown = v == null || (Array.isArray(v) && !v.length) || v === '' ? null
                    : t === 'date' ? fmtDay(v) : Array.isArray(v) ? v.join(', ') : String(v);
                  return [
                    <dt key={`${k}-l`} className="text-[var(--text-secondary)]">{l}</dt>,
                    <dd key={`${k}-v`} className={`truncate ${shown ? 'text-[var(--text-primary)]' : 'text-gray-400'}`}>{shown || (s.data_gaps?.includes(k) ? 'unknown' : '—')}</dd>,
                  ];
                })}
              </dl>
            </div>
          ))}
        </div>
        {s.notes && <p className="mt-3 whitespace-pre-wrap rounded bg-gray-50 p-2 text-xs text-[var(--text-secondary)] dark:bg-slate-800">{s.notes}</p>}
      </Section>

      {/* Contacts */}
      <Section title="Who gets updates">
        <Contacts shipmentId={s.id} contacts={contacts} onChanged={load} />
      </Section>

      {/* Messages */}
      <Section title="Update drafts">
        <ShipmentMessages messages={messages} onChanged={load} emptyText="No drafts. Logging a customer-visible milestone (booked, departed, released, picked up, delivered…) creates one." />
      </Section>

      {/* Mobile-first sticky button (sits above the Feedback / Copilot floaters) */}
      <button onClick={() => withMeta(setLogOpen)}
        className="fixed bottom-20 left-4 right-4 z-40 flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-base font-semibold text-white shadow-lg md:hidden">
        <Truck size={18} /> Log update
      </button>

      {logOpen && meta && <LogUpdateSheet key={s.id} shipment={s} meta={meta} onClose={() => setLogOpen(false)} onSaved={() => { setLogOpen(false); load(); }} />}
      {editOpen && meta && <EditShipmentModal key={s.id} shipment={s} meta={meta} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />}
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <section className="mb-4 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-[var(--text-primary)]">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">{label}</p>
      <p className="font-semibold text-[var(--text-primary)]">{value}</p>
      {sub && <p className="text-xs text-[var(--text-secondary)]">{sub}</p>}
    </div>
  );
}

// Two taps: pick the milestone, then Save. Note and details are optional.
function LogUpdateSheet({ shipment, meta, onClose, onSaved }) {
  const [pick, setPick] = useState(null);
  const [note, setNote] = useState('');
  const [fields, setFields] = useState({});
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const m = meta.milestones.find((x) => x.key === pick);
  const current = meta.milestones.find((x) => x.key === shipment.current_milestone);

  // Next steps first, exceptions after, already-passed steps last.
  const ordered = useMemo(() => {
    const ph = current?.phase ?? -1;
    const next = meta.milestones.filter((x) => x.advances && x.phase > ph);
    const exc = meta.milestones.filter((x) => !x.advances);
    const past = meta.milestones.filter((x) => x.advances && x.phase <= ph);
    return [...next, ...exc, ...past];
  }, [meta, current]);

  async function save() {
    setBusy(true); setErr('');
    try {
      // Only the selected milestone's fields (a field typed under another step is dropped).
      const clean = Object.fromEntries(Object.entries(fields)
        .filter(([k, v]) => (m.fields || []).includes(k) && v !== '' && v != null));
      await api.post(`/shipments/${shipment.id}/events`, {
        milestone: pick, note: note || undefined, fields: clean,
        occurred_at: when ? new Date(when).toISOString() : undefined,
      });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  const inputType = (f) => (['etd', 'eta', 'atd', 'ata', 'cutoff_date', 'last_free_day'].includes(f) ? 'date'
    : ['received_count', 'duty_amount', 'freight_cost'].includes(f) ? 'number' : 'text');

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose}>
      <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white p-4 dark:bg-slate-900 sm:max-w-lg sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-[var(--text-primary)]">{m ? m.label : 'What happened?'}</h3>
          <button onClick={onClose} className="text-gray-400"><X size={18} /></button>
        </div>
        {!m ? (
          <div className="grid grid-cols-2 gap-2">
            {ordered.map((x) => (
              <button key={x.key} onClick={() => setPick(x.key)}
                className={`rounded-xl border px-3 py-3 text-left text-sm font-medium ${x.exception ? 'border-amber-300 text-amber-800 dark:text-amber-300' : 'border-[var(--border)] text-[var(--text-primary)]'} hover:border-primary`}>
                {x.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <textarea autoFocus value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Note (optional): who said what"
              className="w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800" />
            {m.fields?.length > 0 && (
              <details className="rounded-lg border border-[var(--border)] p-2" open={['booked', 'departed', 'eta_changed', 'picked_up'].includes(m.key)}>
                <summary className="cursor-pointer text-xs font-medium text-[var(--text-secondary)]">Details (optional)</summary>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {m.fields.map((f) => (
                    <Field key={f} label={FIELD_LABEL[f] || f.replace(/_/g, ' ')} type={inputType(f)} value={fields[f] ?? ''}
                      placeholder={f === 'container_numbers' ? 'comma separated' : ''}
                      onChange={(e) => setFields({ ...fields, [f]: e.target.value })} />
                  ))}
                </div>
              </details>
            )}
            <Field label="When (leave blank for now)" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
            {err && <p className="text-sm text-red-600">{err}</p>}
            <div className="flex gap-2">
              <button onClick={() => { setPick(null); setFields({}); setErr(''); }} className="rounded-xl border border-[var(--border)] px-4 py-3 text-sm">Back</button>
              <button disabled={busy} onClick={save} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-base font-semibold text-white disabled:opacity-50">
                <Check size={18} /> Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EditShipmentModal({ shipment, meta, onClose, onSaved }) {
  const all = IDENT_GROUPS.flatMap(([, f]) => f);
  const init = Object.fromEntries(all.map(([k]) => [k, Array.isArray(shipment[k]) ? shipment[k].join(', ') : (shipment[k] ?? '')]));
  const [f, setF] = useState({ ...init, notes: shipment.notes || '', data_gaps: (shipment.data_gaps || []).join(', '), lead_id: shipment.lead_id || '', project_id: shipment.project_id || '' });
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // Keep picker load failures visible without changing shipment data.
  const [loadNotes, setLoadNotes] = useState([]);
  const noteLoad = (text) => setLoadNotes((n) => (n.includes(text) ? n : [...n, text]));
  useEffect(() => {
    api.get('/crm/leads').then((r) => setLeads(r.leads || [])).catch(() => noteLoad('Could not load the leads. Close and reopen Edit before changing the linked lead.'));
    api.get('/crm/projects').then((r) => setProjects(r.projects || [])).catch(() => noteLoad('Could not load the watchlist projects. Close and reopen Edit before changing the linked project.'));
  }, []);

  async function save() {
    setBusy(true); setErr('');
    try {
      const patch = {};
      for (const [k, v] of Object.entries(f)) {
        const orig = k === 'notes' ? (shipment.notes || '') : k === 'data_gaps' ? (shipment.data_gaps || []).join(', ')
          : ['lead_id', 'project_id'].includes(k) ? (shipment[k] || '') : init[k];
        if (String(v) !== String(orig)) patch[k] = v === '' ? null : v;
      }
      if (Object.keys(patch).length) await api.patch(`/shipments/${shipment.id}`, patch);
      onSaved();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  const projOpts = projectChoices({ projects, query: q, pickedId: f.project_id });

  return (
    <Modal onClose={onClose} title="Edit shipment" maxWidth="max-w-2xl">
      <div className="space-y-4">
        {IDENT_GROUPS.map(([g, fields]) => (
          <div key={g}>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">{g}</p>
            <div className="grid grid-cols-2 gap-2">
              {fields.map(([k, l, t]) => (t && t.startsWith('enum:') ? (
                <label key={k} className="block text-xs font-medium text-[var(--text-secondary)]">{l}
                  <select value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-2 text-sm dark:bg-slate-800">
                    <option value="">—</option>
                    {(meta.enums[t.slice(5)] || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              ) : (
                <Field key={k} label={l} type={t === 'date' ? 'date' : t === 'number' ? 'number' : 'text'} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
              )))}
            </div>
          </div>
        ))}
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Linked lead
            <select value={f.lead_id} onChange={(e) => setF({ ...f, lead_id: e.target.value })} className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-2 text-sm dark:bg-slate-800">
              <option value="">—</option>
              {leads.map((l) => <option key={l.id} value={l.id}>{l.name}{l.company ? ` · ${l.company}` : ''}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Linked watchlist project
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search…" className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-1 text-xs dark:bg-slate-800" />
            <select value={f.project_id} onChange={(e) => setF({ ...f, project_id: e.target.value })} className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-2 text-sm dark:bg-slate-800">
              <option value="">—</option>
              {projOpts.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
            </select>
          </label>
        </div>
        {loadNotes.map((n) => <p key={n} className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">{n}</p>)}
        <Field label="Still unknown (comma separated field names)" value={f.data_gaps} onChange={(e) => setF({ ...f, data_gaps: e.target.value })} />
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Notes
          <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-2 text-sm dark:bg-slate-800" />
        </label>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm">Cancel</button>
          <button disabled={busy} onClick={save} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">Save</button>
        </div>
      </div>
    </Modal>
  );
}

function Contacts({ shipmentId, contacts, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: '', company: '', role: 'gc_pm', email: '', phone: '' });
  const [err, setErr] = useState('');

  async function add(e) {
    e.preventDefault(); setErr('');
    try {
      await api.post(`/shipments/${shipmentId}/contacts`, Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '')));
      setAdding(false); setF({ name: '', company: '', role: 'gc_pm', email: '', phone: '' }); onChanged();
    } catch (e2) { setErr(e2.message); }
  }

  return (
    <div>
      <div className="space-y-2">
        {contacts.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--border)] p-2 text-sm">
            <div className="min-w-0">
              <p className="font-medium text-[var(--text-primary)]">{c.name}{c.company ? <span className="font-normal text-[var(--text-secondary)]"> · {c.company}</span> : null}</p>
              <p className="text-xs text-[var(--text-secondary)]">{CONTACT_ROLE_LABEL[c.role] || c.role}{c.email ? ` · ${c.email}` : ' · no email on file'}{c.phone ? ` · ${c.phone}` : ''}</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-xs text-[var(--text-secondary)]" title="Only approved contacts can receive updates">
                <input type="checkbox" checked={!!c.approved_to_notify} onChange={async (e) => {
                  setErr('');
                  try { await api.patch(`/shipments/contacts/${c.id}`, { approved_to_notify: e.target.checked }); onChanged(); } catch (e2) { setErr(e2.message); }
                }} />
                <Mail size={12} /> approved to notify
              </label>
              <button onClick={async () => {
                // eslint-disable-next-line no-alert
                if (!window.confirm(`Remove ${c.name}?`)) return;
                setErr('');
                try { await api.delete(`/shipments/contacts/${c.id}`); onChanged(); } catch (e2) { setErr(e2.message); }
              }} className="text-gray-400 hover:text-red-600"><X size={14} /></button>
            </div>
          </div>
        ))}
        {!contacts.length && <p className="text-sm text-[var(--text-secondary)]">No contacts yet.</p>}
      </div>
      {err && !adding && <p className="text-xs text-red-600">{err}</p>}
      {adding ? (
        <form onSubmit={add} className="mt-3 grid grid-cols-2 gap-2 rounded-lg border border-[var(--border)] p-3">
          <Field label="Name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <Field label="Company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} />
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Role
            <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-2 text-sm dark:bg-slate-800">
              {Object.entries(CONTACT_ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <Field label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <Field label="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          <div className="col-span-2 flex items-center justify-end gap-2">
            {err && <span className="mr-auto text-xs text-red-600">{err}</span>}
            <button type="button" onClick={() => { setAdding(false); setErr(''); }} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm">Cancel</button>
            <button disabled={!f.name.trim()} className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">Add</button>
          </div>
        </form>
      ) : (
        <button onClick={() => { setErr(''); setAdding(true); }} className="mt-3 flex items-center gap-1 rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs font-medium"><Plus size={12} /> Add contact</button>
      )}
      <p className="mt-2 text-xs text-[var(--text-secondary)]">Email only for now. SMS / WhatsApp numbers can be stored but nothing sends to them yet.</p>
    </div>
  );
}
